import React, { useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputBase from '@material-ui/core/InputBase';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { useSearchConversation } from 'features/ai-assistant/hooks';
import strings from 'strings';
import { UIStatus } from 'UI/constants/status';

import { useConversationSearchModalStyles } from './ConversationSearchModal.styled';

const COPIES = strings.ai.generators.assistant;

const renderExcerptHtml = excerpt => {
  if (!excerpt) {
    return null;
  }
  return excerpt.includes('**')
    ? excerpt.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    : excerpt;
};

export const ConversationSearchModal = ({ onClose, onSelectResult, open }) => {
  const classes = useConversationSearchModalStyles();
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const { results, uiStatus } = useSearchConversation({ query, debounceMs: 300 });

  const handleClose = () => {
    onClose && onClose();
  };

  const handleSelect = result => {
    onSelectResult && onSelectResult(result);
    handleClose();
  };

  const handleClear = () => {
    setQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
      scroll="paper"
    >
      <Box className={classes.header}>
        <Typography variant="h6">{COPIES.sidebar.searchChat}</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box className={classes.searchInputContainer}>
        <SearchIcon color="disabled" fontSize="small" />
        <InputBase
          autoFocus
          className={classes.searchInput}
          placeholder={COPIES.sidebar.searchChat}
          value={query}
          onChange={event => setQuery(event.target.value)}
          inputRef={inputRef}
          endAdornment={
            query ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label={COPIES.sidebar.clearSearch || COPIES.search.clear}
                  className={classes.clearButton}
                  size="small"
                  onClick={handleClear}
                  onMouseDown={event => event.preventDefault()}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
        />
      </Box>

      <DialogContent className={classes.dialogContent}>
        {uiStatus === UIStatus.Loading && (
          <Box className={classes.statusRow}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="textSecondary">
              {COPIES.search.searching}
            </Typography>
          </Box>
        )}

        {uiStatus === UIStatus.Default && (
          <Typography variant="body2" color="textSecondary">
            {COPIES.search.default}
          </Typography>
        )}

        {uiStatus === UIStatus.Empty && (
          <Typography variant="body2" color="textSecondary">
            {COPIES.search.empty}
          </Typography>
        )}

        {uiStatus === UIStatus.Success && (
          <List disablePadding>
            {results.map(result => (
              <ListItem
                key={result?.date}
                button
                className={classes.resultItem}
                onClick={() => handleSelect(result)}
              >
                <ListItemText
                  primary={result.sessionTitle}
                  secondary={
                    result.excerpt ? (
                      <Typography
                        component="span"
                        variant="body2"
                        dangerouslySetInnerHTML={{ __html: renderExcerptHtml(result.excerpt) }}
                      />
                    ) : null
                  }
                  secondaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};
